import React from "react";
import I18nCoverageBar from "./I18nCoverageBar";

/**
 * LanguageTabs — QUESTIONNAIRE-2.0-A-S1.
 *
 * 18-language tab switcher for the admin builder. Reads the summary
 * payload from `/admin/questionnaires/{qid}/translation-summary` and
 * exposes the active language to the parent via `onChange`.
 *
 * Visual rules per the analysis doc §6:
 *   - Base langs (en/es/zh) pinned first with a divider.
 *   - Per-tab indicator: ✓ 100% reviewed · 🟡 mixed · 🔴 0% · ▒ no scaffold.
 *   - RTL flag from the summary payload (no client-side hardcoding).
 *
 * Persistence: caller is responsible for localStorage. The component
 * itself is stateless apart from the `activeLang` prop.
 *
 * Out of scope for S1: status flip buttons, bulk copy actions,
 * scaffold initialization. Those land in S2-S5.
 */
const TIER_LABELS = {
  en: "Base", es: "Base", zh: "Base",
  it: "Tier 1", fr: "Tier 1", de: "Tier 1", "pt-br": "Tier 1",
  ja: "Tier 2", hi: "Tier 2", ko: "Tier 2",
  ru: "Tier 2", cs: "Tier 2", nl: "Tier 2",
  ar: "RTL", he: "RTL",
  ca: "ES-reg", eu: "ES-reg", gl: "ES-reg",
};

const indicator = (L) => {
  if (L.is_base) return "✓";
  if (!L.has_scaffold) return "▒";
  if (L.completion_percentage >= 100) return "✓";
  if (L.completion_percentage > 0) return "🟡";
  return "🔴";
};

const LanguageTabs = ({ summary, activeLang, onChange, highlightedLangs }) => {
  if (!summary || !Array.isArray(summary.languages)) return null;
  const base = summary.languages.filter((L) => L.is_base);
  const non = summary.languages.filter((L) => !L.is_base);
  const highlightSet = highlightedLangs instanceof Set ? highlightedLangs : new Set(highlightedLangs || []);

  const renderTab = (L) => {
    const isActive = L.language_code === activeLang;
    const isHighlighted = highlightSet.has(L.language_code);
    const testId = `admin-q-builder-lang-tab-${L.language_code}`;
    return (
      <button
        key={L.language_code}
        type="button"
        data-testid={testId}
        data-cascade-highlight={isHighlighted ? "true" : "false"}
        onClick={() => onChange(L.language_code)}
        className={`relative flex flex-col items-start gap-1 rounded-md border px-2.5 py-1.5 text-xs transition-colors
          ${isActive
            ? "border-sky-500 bg-sky-50 text-sky-900 shadow-sm"
            : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}
          ${isHighlighted ? "ring-2 ring-rose-400 ring-offset-1 animate-pulse" : ""}
          ${!L.has_scaffold && !L.is_base ? "opacity-70" : ""}`}
        title={`${L.language_name_native || L.language_code} · ${L.completion_percentage ?? 0}% reviewed${L.direction === "rtl" ? " · RTL" : ""}${isHighlighted ? " · ⚠ needs review (recent cascade)" : ""}`}
      >
        <span className="flex items-center gap-1.5 font-semibold">
          <span>{indicator(L)}</span>
          <span className="uppercase">{L.language_code}</span>
          {L.direction === "rtl" && (
            <span className="rounded bg-purple-100 px-1 text-[9px] font-semibold uppercase text-purple-800">rtl</span>
          )}
          {isHighlighted && (
            <span
              data-testid={`${testId}-cascade-badge`}
              className="rounded bg-rose-600 px-1 text-[9px] font-semibold uppercase text-white"
              title="Recent cascade marked translations as needs_update"
            >
              ⚠ recent
            </span>
          )}
        </span>
        <span className="text-[10px] text-slate-500">
          {L.is_base ? "Base" : `${L.completion_percentage ?? 0}%`}
        </span>
        <div className="w-16">
          <I18nCoverageBar lang={L} compact />
        </div>
      </button>
    );
  };

  return (
    <div data-testid="admin-q-builder-lang-tabs" className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
        <span>Active:</span>
        <span data-testid="admin-q-builder-active-lang" className="font-semibold text-slate-800">{activeLang}</span>
        {summary.languages.find((L) => L.language_code === activeLang)?.direction === "rtl" && (
          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-semibold text-purple-800">RTL</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto" data-testid="admin-q-builder-lang-tabs-row">
        {base.map(renderTab)}
        <span aria-hidden className="mx-1 h-8 w-px bg-slate-300" />
        {non.map(renderTab)}
      </div>

      <div className="flex flex-wrap gap-2 text-[10px] text-slate-500">
        <span>Tier of active: <strong className="text-slate-700">{TIER_LABELS[activeLang] || "—"}</strong></span>
      </div>
    </div>
  );
};

export default LanguageTabs;
